
import { useCallback, useEffect, useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

export type AppRole = Database['public']['Enums']['app_role'];

export function useUserRole() {
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout;

    const fetchRoles = async () => {
      try {
        // Get the current authenticated user first
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        
        if (!mounted) return;

        if (userError) {
          console.error('Error getting user:', userError);
          setRoles([]);
          // Don't set loading to false immediately on error - wait for auth state to settle
          return;
        }

        if (!user) {
          console.log('No authenticated user found');
          setRoles([]);
          setLoading(false);
          return;
        }

        // Query user roles with proper user_id filter
        const { data: roleData, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id);

        if (!mounted) return;

        if (error) {
          console.error('Error fetching user roles:', error);
          setRoles([]);
          setLoading(false);
          return;
        }

        setRoles(roleData?.map(r => r.role) || []);
        setLoading(false);
      } catch (error) {
        console.error('Error in fetchRoles:', error);
        if (mounted) {
          setRoles([]);
          setLoading(false);
        }
      }
    };

    // Listen to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;
      
      console.log('Auth state changed:', event, !!session);
      
      if (event === 'SIGNED_OUT' || !session) {
        setRoles([]);
        setLoading(false);
        return;
      }
      
      if (event === 'SIGNED_IN' || session) {
        // Reset loading state when user signs in
        setLoading(true);
        await fetchRoles();
      }
    });

    // Initial fetch with timeout fallback
    fetchRoles();
    
    // Fallback timeout to prevent infinite loading
    timeoutId = setTimeout(() => {
      if (mounted && loading) {
        console.warn('Auth timeout - setting loading to false');
        setLoading(false);
      }
    }, 5000);

    return () => {
      mounted = false;
      subscription.unsubscribe();
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  const hasRole = useCallback((role: AppRole) => {
    return roles.includes(role);
  }, [roles]);

  const isSuperAdmin = useCallback(() => {
    return hasRole('superadmin');
  }, [hasRole]);

  const isAdmin = useCallback(() => {
    return hasRole('admin') || hasRole('superadmin');
  }, [hasRole]);

  return {
    roles,
    hasRole,
    isAdmin,
    isSuperAdmin,
    loading
  };
}
